import React, { useEffect, useState } from "react";
import { Modal, Form, Input, DatePicker, Select, message, Spin } from "antd";
import styled from "styled-components";
import dayjs from "dayjs";
import { LinkOutlined } from "@ant-design/icons";
import { v4 as uuidv4 } from "uuid";

import { CreateSlotDTO } from "../../../Modules/Slot/Model/Slot";
import { useAppSelector } from "../../../Modules/Presentation/hooks";
import { selectAccounts } from "../../../Modules/Account/Presentation/selectors";
import { selectSlots } from "../../../Modules/Slot/Presentation/selectors";
import { selectUser } from "../../../Modules/User/Presentation/selectors";

const { Option } = Select;

const ModalIntro = styled.div`
  margin-bottom: 18px;
  padding: 14px 16px;
  border-radius: 10px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  color: #475569;
  font-size: 13px;
  line-height: 1.55;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: 16px;
  row-gap: 2px;

  .ant-form-item {
    margin-bottom: 16px;
  }

  .ant-form-item-label > label {
    color: #334155;
    font-weight: 700;
  }

  .ant-input,
  .ant-input-affix-wrapper,
  .ant-picker,
  .ant-select-selector {
    border-radius: 8px !important;
  }

  .ant-picker,
  .ant-select {
    width: 100%;
  }

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const GridField = styled.div<{ $span?: 1 | 2 }>`
  grid-column: span ${({ $span }) => $span ?? 1};

  @media (max-width: 720px) {
    grid-column: span 1;
  }
`;

/* URL 검증 */
const urlValidator = (_: any, value?: string) => {
  if (!value) return Promise.resolve();
  try {
    new URL(value);
    return Promise.resolve();
  } catch {
    return Promise.reject(new Error("올바른 URL 형식이 아닙니다."));
  }
};

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (dto: CreateSlotDTO) => void;
  onBatchSubmit?: (request: { count: number; slots: CreateSlotDTO[] }) => void;
}

const SlotAddModal: React.FC<Props> = ({
  open,
  onClose,
  onSubmit,
  onBatchSubmit,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const slots = useAppSelector(selectSlots);
  const currentUser = useAppSelector(selectUser);

  // 모바일(<=489px) 판별: Select 드롭다운/모달 스크롤 최적화 용도
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 489);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  /** ▸ 계정 분류 */
  const accounts = useAppSelector(selectAccounts);
  const sellers = accounts
    .filter((a) => a.grade === "SELLER")
    .sort((a, b) => a.username.localeCompare(b.username));
  const managers = accounts
    .filter((a) => a.grade === "DISTRIBUTOR" || a.grade === "AGENCY")
    .sort((a, b) => a.username.localeCompare(b.username));

  const isFullUnrestrictedAdmin =
    currentUser?.grade === "ADMIN" && !currentUser?.is_limited_partner_admin;

  useEffect(() => {
    if (open) {
      const logicalNumber = slots.length + 1;
      form.setFieldsValue({
        slot_number: logicalNumber,
        range: [dayjs().add(1, "day"), dayjs().add(10, "day")],
        count: 1,
      });
    }
  }, [open, form, slots.length]);

  /** ▸ 제출 */
  const handleOk = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      const [start, end] = values.range as [dayjs.Dayjs, dayjs.Dayjs];
      const count = Number(values.count || 1);

      const baseDto: Omit<CreateSlotDTO, "slot_number"> = {
        sellerId: values.sellerId,
        manager: values.managerId ?? null,
        main_keyword: values.main_keyword,
        word_keyword: "-",
        mid_single: values.mid_single,
        mid_compare: values.mid_compare ?? "",
        product_url: values.product_url,
        compare_url: values.compare_url ?? "",
        memo: values.memo,
        start_date: start.format("YYYY-MM-DD"),
        end_date: end.format("YYYY-MM-DD"),
        status: "정상",
      };

      // 개수가 1개면 단일 생성, 2개 이상이면 일괄 생성
      if (count === 1) {
        const nextSlotNumber = slots.length + 1;
        const slotNumberWithUuid = `${nextSlotNumber}-${uuidv4()}`;

        const dto: CreateSlotDTO = {
          ...baseDto,
          slot_number: slotNumberWithUuid,
        };

        await onSubmit(dto);
      } else {
        // 일괄 생성
        if (onBatchSubmit) {
          await onBatchSubmit({
            count,
            slots: [
              {
                ...baseDto,
                slot_number: "", // 일괄 생성에서는 서버에서 자동 생성
              },
            ],
          });
        } else {
          // fallback: 기존 방식으로 개별 생성
          const nextSlotNumber = slots.length + 1;

          const results = await Promise.all(
            Array.from({ length: count }).map((_, i) => {
              const slotNumberWithUuid = `${nextSlotNumber + i}-${uuidv4()}`;

              const dto: CreateSlotDTO = {
                ...baseDto,
                slot_number: slotNumberWithUuid,
              };
              return onSubmit(dto);
            }),
          );

          const successCount = results.filter(Boolean).length;
          const failCount = count - successCount;

          if (successCount > 0 && failCount === 0) {
            message.success(`${successCount}개의 슬롯이 추가되었습니다.`);
          } else if (successCount > 0 && failCount > 0) {
            message.warning(
              `${successCount}개 성공, ${failCount}개 실패했습니다.`,
            );
          } else {
            message.error(`슬롯 추가에 실패했습니다.`);
          }
        }
      }

      onClose();
      form.resetFields();
    } catch {
      message.error("필수 항목을 다시 확인해 주세요.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="슬롯 추가"
      open={open}
      onOk={handleOk}
      okText={loading ? "추가 중..." : "추가"}
      onCancel={onClose}
      destroyOnClose
      width={900}
      confirmLoading={loading}
      bodyStyle={
        isMobile
          ? {
              maxHeight: "80vh",
              overflow: "auto",
              overscrollBehavior: "contain" as any,
            }
          : undefined
      }
    >
      <Form form={form} layout="vertical">
        <ModalIntro>
          동일한 설정으로 여러 슬롯을 만들려면 수량을 조정하세요.
        </ModalIntro>

        <FormGrid>
          {/* 셀러 선택 */}
          <GridField>
            <Form.Item
              name="sellerId"
              label="셀러 계정"
              rules={[{ required: true, message: "셀러를 선택하세요." }]}
            >
              <Select
                placeholder="아이디 / 이름 검색"
                showSearch
                optionFilterProp="label"
                optionLabelProp="label"
                getPopupContainer={(trigger) =>
                  trigger.parentElement as HTMLElement
                }
                listHeight={isMobile ? 180 : 256}
                dropdownStyle={
                  isMobile
                    ? {
                        maxHeight: "40vh",
                        overflow: "auto",
                        overscrollBehavior: "contain" as any,
                      }
                    : undefined
                }
                dropdownMatchSelectWidth
              >
                {sellers.map((s) => (
                  <Option
                    key={s.id}
                    value={s.id}
                    label={`${s.username} (${s.name})`}
                  >
                    <div style={{ lineHeight: 1.4 }}>
                      <div>
                        <strong>ID:</strong> {s.username}
                      </div>
                      <div>
                        <strong>이름:</strong> {s.name}
                      </div>
                    </div>
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </GridField>

          {/* 기간 */}
          <GridField>
            <Form.Item
              name="range"
              label="기간"
              rules={[
                { required: true, message: "기간을 선택하세요." },
                {
                  validator: (_, v) => {
                    if (!v || v.length !== 2) {
                      return Promise.reject(
                        new Error("기간을 올바르게 선택하세요."),
                      );
                    }

                    const [s, e] = v;

                    // 일반(비제한형) ADMIN만 오늘·과거 시작 허용. 제한형·그 외는 내일부터
                    if (!isFullUnrestrictedAdmin) {
                      const minStartDate = dayjs().add(1, "day").startOf("day");
                      if (s.isBefore(minStartDate)) {
                        return Promise.reject(
                          new Error("시작일은 내일부터 가능합니다."),
                        );
                      }
                    }

                    if (e.isBefore(s)) {
                      return Promise.reject(
                        new Error("종료일은 시작일과 같거나 이후여야 합니다."),
                      );
                    }

                    return Promise.resolve();
                  },
                },
              ]}
            >
              <DatePicker.RangePicker
                getPopupContainer={(trigger) => document.body}
                popupClassName="slot-add-picker-dropdown"
                disabledDate={(d) => {
                  if (isFullUnrestrictedAdmin) return false;
                  return d.isBefore(
                    dayjs().add(1, "day").startOf("day"),
                    "day",
                  );
                }}
              />
            </Form.Item>
          </GridField>

          <GridField>
            <Form.Item name="main_keyword" label="메인 키워드">
              <Input placeholder="메인 키워드를 입력해주세요." />
            </Form.Item>
          </GridField>

          <GridField>
            <Form.Item
              name="count"
              label="수량"
              initialValue={1}
              rules={[
                { required: true, message: "수량을 입력하세요." },
                {
                  validator: (_, value) => {
                    if (!value || isNaN(value) || value <= 0) {
                      return Promise.reject(
                        new Error("1 이상의 숫자를 입력하세요."),
                      );
                    }
                    if (value > 100) {
                      return Promise.reject(
                        new Error("최대 100개까지 생성 가능합니다."),
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input type="number" min={1} max={100} />
            </Form.Item>
          </GridField>

          <GridField>
            <Form.Item name="mid_single" label="단일 MID">
              <Input placeholder="단일 MID를 입력해주세요." />
            </Form.Item>
          </GridField>

          <GridField>
            <Form.Item
              name="mid_compare"
              label="가격비교 MID"
              rules={[{ message: "MID를 입력하세요." }]}
            >
              <Input placeholder="가격비교 MID를 입력해주세요." />
            </Form.Item>
          </GridField>

          <GridField $span={2}>
            <Form.Item
              name="product_url"
              label="상품 URL"
              rules={[
                { message: "URL을 입력하세요." },
                { validator: urlValidator },
              ]}
            >
              <Input
                prefix={<LinkOutlined />}
                placeholder="상품 URL을 입력해주세요."
              />
            </Form.Item>
          </GridField>

          <GridField $span={2}>
            <Form.Item
              name="compare_url"
              label="가격비교 URL"
              rules={[
                { message: "URL을 입력하세요." },
                { validator: urlValidator },
              ]}
            >
              <Input
                prefix={<LinkOutlined />}
                placeholder="가격비교 URL을 입력해주세요."
              />
            </Form.Item>
          </GridField>

          <GridField $span={2}>
            <Form.Item name="memo" label="메모">
              <Input.TextArea rows={3} placeholder="메모를 입력해주세요." />
            </Form.Item>
          </GridField>
        </FormGrid>
      </Form>
    </Modal>
  );
};

export default SlotAddModal;
